import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  ImageSearch as ImageSearchIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Badge as BadgeIcon,
  SwapHoriz as SwapHorizIcon,
  Style as StyleIcon,
} from '@mui/icons-material';
import { useStore } from 'react-admin';
import type { ColorProperty, ColorPropertyInput, Partner } from '@src/types';
import * as RewareApi from '@src/apis/reware';
import { uploadToS3 } from '@src/apis/reware/catalog/uploadToS3';
import { extractDominantColor } from './colorUtils';

export const EditColorCodeMapping: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentPartner] = useStore<Partner | null>('currentPartner', null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [colorProperty, setColorProperty] = useState<ColorProperty | null>(null);
  const [formData, setFormData] = useState<ColorPropertyInput>({
    code: '',
    display: '',
    hex_code: '',
    url: '',
    synonyms: [],
    family: [],
  });
  const [newSynonym, setNewSynonym] = useState('');
  const [newFamily, setNewFamily] = useState('');
  const [detectImagePreview, setDetectImagePreview] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');
  const colorInputRef = useRef<HTMLInputElement>(null);
  const detectInputRef = useRef<HTMLInputElement>(null);
  const swatchInputRef = useRef<HTMLInputElement>(null);
  const [swatchPreview, setSwatchPreview] = useState<string>('');
  const [swatchUploading, setSwatchUploading] = useState(false);
  const [hexInputMethod, setHexInputMethod] = useState<'enter' | 'select' | 'detect'>('enter');

  useEffect(() => {
    loadColorProperty();
  }, [id, currentPartner]);

  const extractErrorMessage = (error: any, fallback: string): string => {
    if (error?.response?.data?.errors?.[0]?.message) {
      return error.response.data.errors[0].message;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return fallback;
  };

  const loadColorProperty = async () => {
    if (!currentPartner || !id) {
      console.warn('No partner selected or invalid ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      // Fetch all properties across all pages to find the one with matching ID
      const allProperties: ColorProperty[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await RewareApi.getColorProperties({
          partnerCode: currentPartner.code,
          filter: {},
          page: currentPage,
          perPage: 200,
        });

        allProperties.push(...response.data);
        totalPages = Math.ceil(response.total / 200);
        currentPage++;
      } while (currentPage <= totalPages);

      const property = allProperties.find((p) => p.id === parseInt(id));
      if (property) {
        setColorProperty(property);
        setFormData({
          code: property.code,
          display: property.display,
          hex_code: property.hex_code || '',
          url: property.url || '',
          synonyms: property.synonyms || [],
          family: property.family || [],
        });
      } else {
        setColorProperty(null);
      }
    } catch (error) {
      console.error('Failed to load color property:', error);
      setColorProperty(null);
      setLoadError(extractErrorMessage(error, 'Failed to load this color mapping. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/catalog/attributes?tab=colors');
  };

  const handleSave = async () => {
    if (!currentPartner || !id) return;

    setSaving(true);
    setSaveError('');
    try {
      await RewareApi.updateColorProperty({
        id: parseInt(id),
        partnerCode: currentPartner.code,
        token: '', // execAdmin will handle token
        data: formData,
      });

      // Navigate back to list on success
      navigate('/catalog/attributes?tab=colors');
    } catch (error) {
      console.error('Failed to save color property:', error);
      setSaveError(extractErrorMessage(error, 'Failed to save color mapping. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentPartner || !id) return;

    setDeleting(true);
    setDeleteError('');
    try {
      await RewareApi.deleteColorProperty({
        id: parseInt(id),
        partnerCode: currentPartner.code,
        token: '', // execAdmin will handle token
      });

      // Navigate back to list on success
      navigate('/catalog/attributes?tab=colors');
    } catch (error) {
      console.error('Failed to delete color property:', error);
      setDeleteError(extractErrorMessage(error, 'Failed to delete color mapping. Please try again.'));
      setDeleting(false);
    }
  };

  const updateField = (field: keyof ColorPropertyInput, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addSynonym = () => {
    if (newSynonym.trim()) {
      updateField('synonyms', [...(formData.synonyms || []), newSynonym.trim()]);
      setNewSynonym('');
    }
  };

  const removeSynonym = (index: number) => {
    const newSynonyms = [...(formData.synonyms || [])];
    newSynonyms.splice(index, 1);
    updateField('synonyms', newSynonyms);
  };

  const addFamily = () => {
    if (newFamily.trim()) {
      updateField('family', [...(formData.family || []), newFamily.trim()]);
      setNewFamily('');
    }
  };

  const removeFamily = (index: number) => {
    const newFamilies = [...(formData.family || [])];
    newFamilies.splice(index, 1);
    updateField('family', newFamilies);
  };

  const handleDetectImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');

    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be smaller than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setDetectImagePreview(result);

      // Create image element to extract color
      const img = new Image();
      img.onload = () => {
        const hexColor = extractDominantColor(img);
        updateField('hex_code', hexColor);
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const clearDetectImage = () => {
    setDetectImagePreview('');
    if (detectInputRef.current) {
      detectInputRef.current.value = '';
    }
  };

  const handleSwatchUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');

    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be smaller than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setSwatchPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    if (!currentPartner) {
      setUploadError('No partner selected');
      return;
    }

    const ext = file.name.split('.').pop() || 'png';
    const contentType = file.type;

    setSwatchUploading(true);
    try {
      const { uploadUrl, publicUrl } = await RewareApi.getColorSwatchUploadUrl({
        partnerCode: currentPartner.code,
        colorCode: formData.code || 'new',
        fileExtension: ext,
        contentType,
      });

      await uploadToS3({ presignedUrl: uploadUrl, file, contentType });

      updateField('url', publicUrl);
    } catch (error) {
      console.error('Swatch upload failed:', error);
      setUploadError('Failed to upload swatch image. Please try again.');
      setSwatchPreview('');
    } finally {
      setSwatchUploading(false);
    }
  };

  const clearSwatchImage = () => {
    updateField('url', '');
    setSwatchPreview('');
    if (swatchInputRef.current) {
      swatchInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!colorProperty) {
    return (
      <Box sx={{ p: 3 }}>
        {loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <Typography variant="h5" color="error">
            Color property not found
          </Typography>
        )}
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Color Mappings
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon /> Edit Color Code Mapping
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Color Mappings
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Map a catalog code to its display name, grouping, and storefront presentation
      </Typography>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {saveError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError('')}>
              {saveError}
            </Alert>
          )}

          {/* Color Identity */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon /> Color Identity
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The catalog code and the customer-facing name it maps to.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Color Code"
                required
                fullWidth
                value={formData.code}
                onChange={(e) => updateField('code', e.target.value)}
                helperText='The code used in your catalog data (e.g., "NVY", "001")'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Color Display Name"
                required
                fullWidth
                value={formData.display}
                onChange={(e) => updateField('display', e.target.value)}
                helperText='Customer-facing color name (e.g., "Midnight Blue")'
              />
            </Grid>
          </Grid>

          {/* Color Presentation */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaletteIcon /> Color Presentation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set the hex value and optional swatch image shown on the storefront.
          </Typography>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
              {uploadError}
            </Alert>
          )}

          {/* Current Hex Code Display */}
          <Card variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: formData.hex_code || '#000000',
                  border: '2px solid #e0e0e0',
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                  CURRENT HEX CODE
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {formData.hex_code || '#000000'}
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Hex Code Input Methods */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how you&apos;d like to set the hex code:
          </Typography>

          <Box sx={{ display: 'flex', gap: 0, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Button
              variant={hexInputMethod === 'enter' ? 'contained' : 'outlined'}
              onClick={() => setHexInputMethod('enter')}
              sx={{
                flex: 1,
                borderRadius: 0,
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
                border: 'none',
                '&.MuiButton-outlined': {
                  border: 'none',
                  backgroundColor: 'transparent',
                },
              }}
              startIcon={<EditIcon />}
            >
              Enter a hex code
            </Button>
            <Button
              variant={hexInputMethod === 'select' ? 'contained' : 'outlined'}
              onClick={() => setHexInputMethod('select')}
              sx={{
                flex: 1,
                borderRadius: 0,
                border: 'none',
                borderLeft: '1px solid',
                borderRight: '1px solid',
                borderColor: 'divider',
                '&.MuiButton-outlined': {
                  border: 'none',
                  borderLeft: '1px solid',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'transparent',
                },
              }}
              startIcon={<PaletteIcon />}
            >
              Select a color
            </Button>
            <Button
              variant={hexInputMethod === 'detect' ? 'contained' : 'outlined'}
              onClick={() => setHexInputMethod('detect')}
              sx={{
                flex: 1,
                borderRadius: 0,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
                border: 'none',
                '&.MuiButton-outlined': {
                  border: 'none',
                  backgroundColor: 'transparent',
                },
              }}
              startIcon={<ImageSearchIcon />}
            >
              Detect from image
            </Button>
          </Box>

          {/* Enter Hex Code Method */}
          {hexInputMethod === 'enter' && (
            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
              <TextField
                label="Hex Code"
                fullWidth
                value={formData.hex_code}
                onChange={(e) => updateField('hex_code', e.target.value)}
                placeholder="#000000"
                helperText="Enter a hex code like #FF5733"
              />
            </Card>
          )}

          {/* Select Color Method */}
          {hexInputMethod === 'select' && (
            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
              <input
                ref={colorInputRef}
                type="color"
                value={formData.hex_code || '#000000'}
                onChange={(e) => updateField('hex_code', e.target.value)}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<PaletteIcon />}
                onClick={() => colorInputRef.current?.click()}
                fullWidth
              >
                Open Color Picker
              </Button>
            </Card>
          )}

          {/* Detect from Image Method */}
          {hexInputMethod === 'detect' && (
            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload an image and we&apos;ll automatically detect the dominant color
              </Typography>
              <input
                ref={detectInputRef}
                type="file"
                accept="image/*"
                onChange={handleDetectImageUpload}
                style={{ display: 'none' }}
              />
              {!detectImagePreview ? (
                <Button
                  variant="outlined"
                  startIcon={<ImageSearchIcon />}
                  onClick={() => detectInputRef.current?.click()}
                  fullWidth
                >
                  Upload Image to Detect Color
                </Button>
              ) : (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={detectImagePreview}
                    alt="Color detection"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={clearDetectImage}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { backgroundColor: 'background.paper' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Card>
          )}

          {/* Color Swatch Image */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StyleIcon /> Color Swatch Image
          </Typography>
            <input
              ref={swatchInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleSwatchUpload}
              style={{ display: 'none' }}
            />
            {formData.url || swatchPreview ? (
              <Box>
                <Box
                  sx={{
                    width: 300,
                    p: 2,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: '#f5f5f5',
                    textAlign: 'center',
                    position: 'relative',
                  }}
                >
                  <img
                    src={formData.url || swatchPreview}
                    alt="Color swatch"
                    style={{
                      maxWidth: '100px',
                      maxHeight: '100px',
                      borderRadius: '8px',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={clearSwatchImage}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { backgroundColor: 'background.paper' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    SWATCH
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                onClick={() => swatchInputRef.current?.click()}
                sx={{
                  width: 300,
                  p: 4,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: '#f5f5f5',
                  textAlign: 'center',
                  cursor: swatchUploading ? 'default' : 'pointer',
                  transition: 'border-color 0.2s',
                  '&:hover': swatchUploading ? {} : { borderColor: 'primary.main' },
                }}
              >
                {swatchUploading ? (
                  <CircularProgress size={32} />
                ) : (
                  <>
                    <ImageSearchIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Click to upload a swatch
                    </Typography>
                  </>
                )}
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, fontWeight: 'bold' }}>
                  SWATCH
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
              Optional. Used in place of the hex swatch on the storefront. PNG or JPG, up to 5 MB.
            </Typography>

          {/* Color Groups */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaletteIcon /> Color Groups
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Groups drive storefront color filtering. Add as many as apply.
          </Typography>

          <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {formData.family?.map((group, index) => (
                <Chip key={index} label={group} onDelete={() => removeFamily(index)} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Add color group"
                value={newFamily}
                onChange={(e) => setNewFamily(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addFamily()}
                fullWidth
              />
              <Button onClick={addFamily} variant="outlined">
                Add
              </Button>
            </Box>
          </Card>

          {/* Color Synonyms */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon /> Color Synonyms
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Other color codes that should map to this color.
          </Typography>

          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {formData.synonyms?.map((synonym, index) => (
                <Chip key={index} label={synonym} onDelete={() => removeSynonym(index)} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Add synonym"
                value={newSynonym}
                onChange={(e) => setNewSynonym(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addSynonym()}
                fullWidth
              />
              <Button onClick={addSynonym} variant="outlined">
                Add
              </Button>
            </Box>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={handleBack}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !formData.code || !formData.display}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => (!deleting ? setDeleteDialogOpen(false) : undefined)}>
        <DialogTitle>Delete Color Code Mapping?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete the color code mapping for &quot;{formData.code}&quot;? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
